import { useState, useEffect } from 'react';

export interface VariantBanner {
  icon: string;
  title?: string;
  description: string;
}

export interface VariantConfig {
  h1?: string;
  banner?: VariantBanner;
  cta?: string;
  disableSplitScreen?: boolean;
}

const parseVariantBanner = (value: unknown): VariantBanner | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const record = value as Record<string, unknown>;

  if (typeof record.description !== 'string' || typeof record.icon !== 'string') {
    return undefined;
  }

  const banner: VariantBanner = { icon: record.icon, description: record.description };

  if (typeof record.title === 'string') {
    banner.title = record.title;
  }

  return banner;
};

const parseVariantConfig = (data: unknown): VariantConfig | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const record = data as Record<string, unknown>;
  const config: VariantConfig = {};

  if (typeof record.h1 === 'string') {
    config.h1 = record.h1;
  }

  const banner = parseVariantBanner(record.banner);
  if (banner) {
    config.banner = banner;
  }

  if (typeof record.cta === 'string') {
    config.cta = record.cta;
  }

  if (typeof record.disableSplitScreen === 'boolean') {
    config.disableSplitScreen = record.disableSplitScreen;
  }

  return config;
};

export const useVariantConfig = (variant: string | null): { variantConfig: VariantConfig | null; isLoading: boolean } => {
  const [variantConfig, setVariantConfig] = useState<VariantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(!!variant);

  useEffect(() => {
    if (!variant) {
      setVariantConfig(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const abortController = new AbortController();

    const fetchVariantConfig = async () => {
      try {
        const response = await fetch(`/assets/variants/${variant}`, { signal: abortController.signal });
        if (!response.ok) {
          return;
        }

        const data: unknown = await response.json();
        const config = parseVariantConfig(data);
        if (config) {
          setVariantConfig(config);
        }
      } catch {
        // Revert to default behavior on any error (including abort)
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchVariantConfig();

    return () => {
      abortController.abort();
    };
  }, [variant]);

  return { variantConfig, isLoading };
};
